import { useState, useEffect, useCallback } from 'react';

const useNotifications = (currentUser) => {
  const [notifications, setNotifications] = useState([]);

  // Generate notification ID
  const generateId = () => Date.now() + Math.random();

  // Add notification with user filtering
  const addNotification = useCallback((notification) => {
    // Safety check - return early if currentUser is not available
    if (!currentUser || !currentUser.name) {
      console.warn('Cannot add notification: currentUser not available');
      return;
    }
    
    // Only add notification if it's for this user/organization
    const isForThisUser = 
      !notification.targetUser || 
      notification.targetUser === currentUser.name ||
      notification.targetOrg === currentUser.organization;
    
    if (!isForThisUser) return;
    
    const newNotification = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      read: false,
      userId: currentUser.name,
      userOrg: currentUser.organization,
      ...notification
    };
    setNotifications(prev => [newNotification, ...prev]);
    return newNotification.id;
  }, [currentUser]);

  // Mark as read
  const markAsRead = useCallback((id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Remove specific notification
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Notification templates with user targeting
  const templates = {
    reservationSubmitted: (propCount, orgName) => ({
      type: 'reservation_submitted',
      title: 'Reservation Request Submitted',
      message: `Your request for ${propCount} props from ${orgName} has been submitted and is pending review.`,
      priority: 'normal',
      targetUser: currentUser?.name // Only for the requesting user
    }),

    reservationApproved: (propCount, orgName, pickupDate, targetUser) => ({
      type: 'reservation_approved', 
      title: 'Reservation Approved!',
      message: `Your request for ${propCount} props from ${orgName} has been approved. Available for pickup ${pickupDate ? `on ${pickupDate}` : 'now'}.`,
      priority: 'high',
      targetUser: targetUser // Only for the requesting user
    }),

    reservationRejected: (propCount, orgName, reason, targetUser) => ({
      type: 'reservation_rejected',
      title: 'Reservation Declined',
      message: `Your request for ${propCount} props from ${orgName} was declined. ${reason ? `Reason: ${reason}` : ''}`,
      priority: 'normal',
      targetUser: targetUser // Only for the requesting user
    }),

    reservationReady: (propCount, orgName, targetUser) => ({
      type: 'reservation_ready',
      title: 'Props Ready for Pickup',
      message: `${propCount} props from ${orgName} are checked out and ready for pickup.`,
      priority: 'high',
      targetUser: targetUser // Only for the requesting user
    }),

    returnReminder: (propCount, daysUntilDue) => ({
      type: 'return_reminder',
      title: 'Return Reminder',
      message: `You have ${propCount} props due for return in ${daysUntilDue} days.`,
      priority: 'medium'
    }),

    overdue: (propCount, daysOverdue) => ({
      type: 'overdue',
      title: 'Props Overdue',
      message: `You have ${propCount} props that are ${daysOverdue} days overdue. Please return them as soon as possible.`,
      priority: 'urgent'
    }),

    transferComplete: (propName, fromOrg) => ({
      type: 'transfer_complete',
      title: 'Transfer Complete',
      message: `"${propName}" has been successfully transferred from ${fromOrg} to your organization.`,
      priority: 'normal'
    })
  };

  // Helper functions to add specific notification types with user targeting
  const notifyReservationSubmitted = (propCount, orgName) => 
    addNotification(templates.reservationSubmitted(propCount, orgName));

  const notifyReservationApproved = (propCount, orgName, pickupDate, targetUser) =>
    addNotification(templates.reservationApproved(propCount, orgName, pickupDate, targetUser));

  const notifyReservationRejected = (propCount, orgName, reason, targetUser) =>
    addNotification(templates.reservationRejected(propCount, orgName, reason, targetUser));

  const notifyReservationReady = (propCount, orgName, targetUser) =>
    addNotification(templates.reservationReady(propCount, orgName, targetUser));

  const notifyReturnReminder = (propCount, daysUntilDue) =>
    addNotification(templates.returnReminder(propCount, daysUntilDue));

  const notifyOverdue = (propCount, daysOverdue) =>
    addNotification(templates.overdue(propCount, daysOverdue));

  const notifyTransferComplete = (propName, fromOrg) =>
    addNotification(templates.transferComplete(propName, fromOrg));

  // Clear notifications when user changes
  useEffect(() => {
    if (currentUser?.name && currentUser?.organization) {
      setNotifications([]);
    }
  }, [currentUser?.name, currentUser?.organization]);

  // Check for overdue items on mount and periodically
  useEffect(() => {
    // Safety check
    if (!currentUser?.name) return;
    
    // This would typically check against database for user's checked out props
    // For now, we'll create some sample notifications for testing
    
    // Simulate checking for due dates
    const checkDueDates = () => {
      // This would query the database for the user's checked out props
      // and create notifications for items due soon or overdue
    };

    checkDueDates();
    
    // Check every hour
    const interval = setInterval(checkDueDates, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser?.name, addNotification]);

  return {
    notifications,
    addNotification,
    markAsRead,
    clearAll,
    removeNotification,
    // Convenience methods
    notifyReservationSubmitted,
    notifyReservationApproved,
    notifyReservationRejected,
    notifyReservationReady,
    notifyReturnReminder,
    notifyOverdue,
    notifyTransferComplete
  };
};

export default useNotifications;